// Black-Scholes with zero rates, ported from server/src/pricing.ts so preview quotes match live ones.

const YEAR = 365 * 24 * 3600;

export const PRICING = {
  ivFloor: 1.5,
  ivCap: 6,
  ivMultiplier: 1.25,
  askSpread: 0.1,
  bidSpread: 0.1,
  minPremiumBpsOfNotional: 25,
  bandBps: 250,
};

export function normCdf(x: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989422804014327 * Math.exp((-x * x) / 2);
  const p = d * t * (0.31938153 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  return x >= 0 ? 1 - p : p;
}

function d1(S: number, K: number, tSec: number, sigma: number): number {
  const vt = sigma * Math.sqrt(Math.max(tSec, 1) / YEAR);
  return (Math.log(S / K) + (vt * vt) / 2) / vt;
}

export function blackScholes(S: number, K: number, tSec: number, sigma: number, isCall: boolean): number {
  if (S <= 0 || K <= 0) return 0;
  const vt = sigma * Math.sqrt(Math.max(tSec, 1) / YEAR);
  const a = d1(S, K, tSec, sigma);
  const b = a - vt;
  return isCall ? S * normCdf(a) - K * normCdf(b) : K * normCdf(-b) - S * normCdf(-a);
}

export function delta(S: number, K: number, tSec: number, sigma: number, isCall: boolean): number {
  const n = normCdf(d1(S, K, tSec, sigma));
  return isCall ? n : n - 1;
}

export function quoteVol(realized: number): number {
  return Math.min(PRICING.ivCap, Math.max(PRICING.ivFloor, realized * PRICING.ivMultiplier));
}

/** Vault ask per option in ETH: model price plus spread, floored at a share of notional and at intrinsic. */
export function askPerUnit(spot: number, strike: number, tSec: number, sigma: number, isCall: boolean): number {
  const band = PRICING.bandBps / 10_000;
  const model = blackScholes(spot, strike, tSec, sigma, isCall) * (1 + PRICING.askSpread);
  const floor = (spot * PRICING.minPremiumBpsOfNotional) / 10_000;
  const intrinsic = isCall ? Math.max(0, spot * (1 + band) - strike) : Math.max(0, strike - spot * (1 - band));
  return Math.max(model, floor, intrinsic * 1.002);
}

/** Mirrors the server's buyback bid (before the vault's on-chain cap). */
export function bidPerUnit(spot: number, strike: number, tSec: number, sigma: number, isCall: boolean): number {
  const band = PRICING.bandBps / 10_000;
  const model = blackScholes(spot, strike, tSec, sigma, isCall) * (1 - PRICING.bidSpread);
  const intrinsic = isCall ? Math.max(0, spot * (1 - band) - strike) : Math.max(0, strike - spot * (1 + band));
  return Math.max(0, model, intrinsic * 0.998);
}
